import React, { useState } from "react";
import {
  Box,
  Fade,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useTranslation } from "react-i18next";
import { useLoginStatus } from "../../../context/LoginStatusContext";
import OrganizationInfo from "./components/OrganizationInfo";
import MemberManagement from "./components/MemberManagement";
import OrganizationSettings from "./components/OrganizationSettings";
import UsageReport from "./components/UsageReport";
import { alpha } from "@mui/material/styles";

type SectionKey = "info" | "members" | "settings" | "usage";

interface SectionConfig {
  key: SectionKey;
  title: string;
  icon: React.ReactNode;
  component: React.ReactNode;
  canView: boolean;
}

const Enterprise = () => {
  const { t } = useTranslation();
  const { user } = useLoginStatus();
  const theme = useTheme();

  // アクティブなセクションの状態管理
  const [activeSection, setActiveSection] = useState<SectionKey>("info");

  // 早期リターンの前にすべてのステート定義を完了させる
  const isInEnterpriseMode = user?.user_type === "enterprise";

  // 権限とセクション可視性の初期値（非エンタープライズモードではすべてfalse）
  const isOwner = isInEnterpriseMode ? user?.enterprise?.role === "owner" : false;
  const isManager = isInEnterpriseMode ? user?.enterprise?.role === "manager" : false;
  const permissions = isInEnterpriseMode ? user?.enterprise?.permissions || [] : [];

  const canViewMembers =
    isInEnterpriseMode &&
    (isOwner || isManager || permissions.includes("invite_members") || permissions.includes("view_invitations"));
  const canViewSettings =
    isInEnterpriseMode &&
    (isOwner || permissions.includes("edit_organization_settings") || permissions.includes("edit_partial_settings"));
  const canViewUsage = isInEnterpriseMode && (isOwner || isManager || permissions.includes("view_all_usage"));

  // セクション設定
  const sections: SectionConfig[] = [
    {
      key: "info",
      title: t("preferences.enterprise.info"),
      icon: <InfoIcon fontSize="small" sx={{ color: "primary.main" }} />,
      component: <OrganizationInfo />,
      canView: isInEnterpriseMode,
    },
    {
      key: "members",
      title: t("preferences.enterprise.members"),
      icon: <PeopleIcon fontSize="small" sx={{ color: "primary.main" }} />,
      component: <MemberManagement />,
      canView: canViewMembers,
    },
    {
      key: "settings",
      title: t("preferences.enterprise.settings"),
      icon: <SettingsIcon fontSize="small" sx={{ color: "primary.main" }} />,
      component: <OrganizationSettings />,
      canView: canViewSettings,
    },
    {
      key: "usage",
      title: t("preferences.enterprise.usage"),
      icon: <AssessmentIcon fontSize="small" sx={{ color: "primary.main" }} />,
      component: <UsageReport />,
      canView: canViewUsage,
    },
  ];

  // ユーザーがアクセスできるセクションのみをフィルタリング
  const availableSections = sections.filter((section) => section.canView);

  // 最初に表示するセクションを設定（useEffectはコンポーネントのトップレベルで宣言）
  React.useEffect(() => {
    if (availableSections.length > 0 && !availableSections.some((section) => section.key === activeSection)) {
      setActiveSection(availableSections[0].key);
    }
  }, [availableSections, activeSection]);

  // ユーザーがエンタープライズモードでない場合の早期リターン
  if (!isInEnterpriseMode) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">{t("preferences.enterprise.notEnterpriseMode")}</Typography>
      </Box>
    );
  }

  // 現在アクティブなセクションのコンポーネントを取得
  const activeComponent = sections.find((section) => section.key === activeSection)?.component;

  // セクションのタイトルを取得
  const activeTitle = sections.find((section) => section.key === activeSection)?.title || "";

  return (
    <Box sx={{ width: "100%", py: 4 }}>
      <Grid container spacing={3}>
        {/* サイドバー */}
        <Grid
          size={{
            xs: 12,
            md: 3,
            lg: 2.5,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.4),
              borderRadius: 1,
              overflow: "hidden",
              position: "sticky",
              top: 64,
            }}
          >
            <List component="nav" sx={{ p: 0 }}>
              {availableSections.map((section) => (
                <ListItemButton
                  key={section.key}
                  selected={activeSection === section.key}
                  onClick={() => setActiveSection(section.key)}
                  sx={{
                    cursor: "pointer",
                    borderLeft:
                      activeSection === section.key
                        ? `4px solid ${theme.palette.primary.main}`
                        : "4px solid transparent",
                    "&.Mui-selected": {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    },
                    "&:hover": {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "40px" }}>{section.icon}</ListItemIcon>
                  <ListItemText primary={section.title} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* コンテンツエリア */}
        <Grid
          size={{
            xs: 12,
            md: 9,
            lg: 9.5,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 0,
              overflow: "hidden",
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.4),
              transition: "all 0.3s",
              height: "100%",
            }}
          >
            <Box sx={{ px: 2, py: 1, bgcolor: "primary.dark", color: "white", display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" sx={{ ml: 1 }} component="h6">
                {activeTitle}
              </Typography>
            </Box>
            <Fade in={true} timeout={500} key={activeSection}>
              <Box sx={{ p: 3 }}>{activeComponent}</Box>
            </Fade>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Enterprise;
